import {
  clampThrottle,
  generateThrottleCurveDomain,
  getMeasuredThrottleBounds,
} from "./calculator-state";
import { CubicSpline } from "./spline";
import type { BoatDataPoint, InterpolatedPerformance } from "./types";

export class RangeEngine {
  private splineRpm: CubicSpline;
  private splineWattage: CubicSpline;
  private splineSpeed: CubicSpline;
  public minThrottle: number;
  public maxThrottle: number;

  constructor(dataPoints: BoatDataPoint[]) {
    const sorted = [...dataPoints].sort((a, b) => a.throttle - b.throttle);
    const { minThrottle, maxThrottle } = getMeasuredThrottleBounds(sorted);

    this.minThrottle = minThrottle;
    this.maxThrottle = maxThrottle;

    // Pad with zero point if needed
    if (sorted.length > 0 && sorted[0].throttle > 0) {
      sorted.unshift({ throttle: 0, rpm: 0, wattage: 0, speed: 0 });
    }

    const throttles = sorted.map((d) => d.throttle);
    const rpms = sorted.map((d) => d.rpm);
    const wattages = sorted.map((d) => d.wattage);
    const speeds = sorted.map((d) => d.speed);

    this.splineRpm = new CubicSpline(throttles, rpms);
    this.splineWattage = new CubicSpline(throttles, wattages);
    this.splineSpeed = new CubicSpline(throttles, speeds);
  }

  interpolate(throttle: number, batteryWh: number): InterpolatedPerformance {
    if (!Number.isFinite(throttle) || throttle <= 0 || this.maxThrottle <= 0) {
      return { rpm: 0, wattage: 0, speed: 0, rangeNm: 0, durationHours: 0 };
    }

    const boundedThrottle = clampThrottle(
      throttle,
      this.minThrottle,
      this.maxThrottle
    );
    const rpm = Math.max(0, this.splineRpm.evaluate(boundedThrottle));
    const wattage = Math.max(0, this.splineWattage.evaluate(boundedThrottle));
    const speed = Math.max(0, this.splineSpeed.evaluate(boundedThrottle));

    const durationHours = wattage > 0 ? batteryWh / wattage : 0;
    const rangeNm = durationHours * speed;

    return { rpm, wattage, speed, rangeNm, durationHours };
  }

  generateCurve(
    batteryWh: number,
    steps: number = 100
  ): {
    throttle: number;
    rpm: number;
    wattage: number;
    speed: number;
    rangeNm: number;
  }[] {
    const result: {
      throttle: number;
      rpm: number;
      wattage: number;
      speed: number;
      rangeNm: number;
    }[] = [];

    for (const t of generateThrottleCurveDomain(
      this.minThrottle,
      this.maxThrottle,
      steps
    )) {
      const perf = this.interpolate(t, batteryWh);
      result.push({
        throttle: Math.round(t * 10) / 10,
        rpm: perf.rpm,
        wattage: perf.wattage,
        speed: perf.speed,
        rangeNm: perf.rangeNm,
      });
    }

    return result;
  }
}

export function haversineNm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3440.065; // Earth radius in nautical miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function computeRouteDistance(
  start: { lat: number; lng: number },
  waypoints: { lat: number; lng: number }[]
): number {
  let total = 0;
  let prev = start;
  for (const wp of waypoints) {
    total += haversineNm(prev.lat, prev.lng, wp.lat, wp.lng);
    prev = wp;
  }
  return total;
}
